import axios from 'axios';

let apiUrl = '/api';
let runtimeConfig = {};

// En producción, cargar la URL desde config.json (runtime config)
export async function loadConfig() {
    try {
        const response = await fetch('/config.json');
        const config = await response.json();
        runtimeConfig = config;
        if (config.apiUrl) {
            apiUrl = config.apiUrl;
            api.defaults.baseURL = apiUrl;
        }
    } catch {
        console.warn('No se pudo cargar config.json, usando URL por defecto');
    }
}

export function getConfig() {
    return runtimeConfig;
}

const api = axios.create({
    baseURL: apiUrl,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Interceptor: agregar JWT token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Interceptor: manejar errores y refresh token
let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token);
        }
    });
    failedQueue = [];
}

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            // Si no hay tokens, el usuario es anónimo — no redirigir, solo rechazar
            const accessToken = localStorage.getItem('accessToken');
            const refreshToken = localStorage.getItem('refreshToken');
            if (!accessToken && !refreshToken) {
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then((token) => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return api(originalRequest);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            if (refreshToken) {
                try {
                    const response = await axios.post(`${apiUrl}/auth/refresh`, {
                        refreshToken
                    });

                    const { accessToken: newAccessToken, refreshToken: newRefreshToken, usuario } = response.data;
                    localStorage.setItem('accessToken', newAccessToken);
                    localStorage.setItem('refreshToken', newRefreshToken);

                    // Actualizar el store si está disponible
                    try {
                        const { useUserStore } = await import('@/stores/userStore');
                        const userStore = useUserStore();
                        userStore.setAuth({ accessToken: newAccessToken, refreshToken: newRefreshToken, usuario });
                    } catch {
                        // Store might not be initialized yet
                    }

                    processQueue(null, newAccessToken);
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                    return api(originalRequest);
                } catch (refreshError) {
                    processQueue(refreshError, null);

                    if (refreshError.response) {
                        // Servidor rechazó el refresh token — sesión inválida, logout
                        localStorage.removeItem('accessToken');
                        localStorage.removeItem('refreshToken');
                        window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
                    }

                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            } else {
                // Tiene accessToken pero no refreshToken — sesión inválida
                localStorage.removeItem('accessToken');
                window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
            }
        }

        return Promise.reject(error);
    }
);

export default api;
